import { useId } from "react";

import Stack from "@mui/material/Stack";
import { FormType } from "@simplyk/common";
import { FieldValues, Path, PathValue, useController, useWatch, UseFormReturn } from "react-hook-form";

import { useFormV2Context } from "@/canvas/features/FormV2/contexts/FormV2Context";
import { AmplitudeEvents } from "@/common/constants/amplitude";
import { getErrorMessages } from "@/common/helpers/reactHookForm";
import { isSweepstakesCategory } from "@/common/helpers/sweepstakes";
import { isCompanyNameValid, isRequired } from "@/common/helpers/validators";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/common/hooks/useTranslate";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { usePreviewContext } from "@/features/LiveFormEditor/LivePreview/contexts/PreviewContext";
import { FormCheckbox } from "@/forms-design-system/FormCheckbox";
import { FormTextField } from "@/forms-design-system/FormTextField/FormTextField";

export type GiveAsOrganismProps<T extends FieldValues> = {
  form: UseFormReturn<T>;
  amplitudePayload: Record<string, unknown>;
  formType: FormType;
  helperText?: string;
};

export const GiveAsSection = <T extends FieldValues>({
  form,
  amplitudePayload,
  formType,
}: React.PropsWithChildren<GiveAsOrganismProps<T>>) => {
  const { t } = useTranslate();
  const { formCategory } = useFormV2Context();
  const { logAmplitudeEventCallback } = useAmplitude();
  const checked = useWatch({ control: form.control, name: "giveAsOrganism" as Path<T> });
  const hasGiftAidConfirmation = useWatch({ control: form.control, name: "hasGiftAidConfirmation" as Path<T> });
  const errors = form.formState.errors;

  const {
    field: { onChange, ...rest },
  } = useController({
    control: form.control,
    name: "giveAsOrganism" as Path<T>,
  });

  const handleChange = (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    onChange(checked);
    // The company field unmounts when unchecked but its value lingers in form state; drop it so a
    // retracted company name is never submitted as a corporate payment.
    if (!checked) {
      form.setValue("companyName" as Path<T>, "" as PathValue<T, Path<T>>);
    }
    logAmplitudeEventCallback(AmplitudeEvents.DonorFormInputChanged, {
      label: "giveAsOrganism",
      errors: getErrorMessages({
        keys: Object.keys(errors),
        errors,
      }),
      ...amplitudePayload,
    });
  };

  return (
    <Stack gap={1}>
      <FormCheckbox
        {...rest}
        id={useId()}
        onChange={handleChange}
        label={
          formType !== FormType.Ticketing
            ? t("donationForm", "giveAsOrganism")
            : isSweepstakesCategory(formCategory)
              ? t("ticketing", "sweepstakes.giveAsOrganism")
              : t("ticketing", "giveAsOrganism")
        }
      />

      {checked && (
        <GiveAsSectionCompanyName
          form={form}
          amplitudePayload={amplitudePayload}
          formType={formType}
          helperText={hasGiftAidConfirmation && t("common", "giveAsOrganismSwiftAidHelperText")}
        />
      )}
    </Stack>
  );
};

const GiveAsSectionCompanyName = <T extends FieldValues>({
  form,
  amplitudePayload,
  helperText,
}: React.PropsWithChildren<GiveAsOrganismProps<T>>) => {
  const { isPreview } = usePreviewContext();
  const { t } = useTranslate();
  const { logAmplitudeEvent } = useAmplitude();

  const errors = form.formState.errors;

  const handleBlur = () => {
    logAmplitudeEvent(AmplitudeEvents.DonorFormInputChanged, {
      label: "companyName",
      errors: getErrorMessages({ keys: Object.keys(errors), errors }),
      ...amplitudePayload,
    });
  };

  return (
    <FormTextField
      control={form.control}
      name={"companyName" as Path<T>}
      rules={[
        isRequired(t("ticketing", "companyNameRequired")),
        isCompanyNameValid(t("common", "invalidCharacterUsed")),
      ]}
      type="text"
      label={t("ticketing", "companyName")}
      data-test="ticket-form-companyname"
      disableInjectingGlobalStyles={isPreview}
      onBlur={handleBlur}
      error={!!errors.companyName}
      helperText={(errors.companyName?.message as string) || helperText}
      required
      autoFocus
    />
  );
};
